<template>
  <div>
    <b-row>
      <b-col cols="3">
        <slot></slot>

        <b-form-group id="input-group-name" label="Name" label-for="input-name">
          <b-form-input
            id="input-name"
            v-model.trim="form.name"
            v-validate="{ required: true, max: 100 }"
            placeholder="Name"
            data-vv-name="name"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          id="input-group-description"
          label="Description"
          label-for="input-description"
        >
          <b-form-textarea
            id="input-description"
            v-model.trim="form.description"
            v-validate="{ max: 500 }"
            placeholder="Description"
            rows="2"
            data-vv-name="description"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group
          id="input-group-range"
          label="Range"
          label-for="input-range"
        >
          <Slider
            v-if="yearRange"
            v-model="years"
            :min="yearRange[0]"
            :max="yearRange[1]"
          ></Slider>
        </b-form-group>

        <b-form-group
          id="input-group-type"
          label="Chart type"
          label-for="input-type"
          class="chart-type-group"
        >
          <b-form-radio
            v-for="type in typeOptions"
            :key="type.value"
            v-model="form.type"
            v-b-popover.hover.top="type.text"
            v-validate="{ required: true }"
            :value="type.value"
            :class="[
              'chart-type chart-type-' + type.value,
              { selected: form.type === type.value }
            ]"
            data-vv-name="chart type"
            >{{ type.text }}</b-form-radio
          >
        </b-form-group>
      </b-col>

      <b-col v-show="form.type" cols="3">
        <div class="section-header">
          <h1 class="heading">{{ xLabel() }}</h1>

          <div class="actions"></div>
        </div>
        <ExploreAnalysisCreateAxis
          :axis="axis.x"
          :type="form.type"
          :multiple="true"
        ></ExploreAnalysisCreateAxis>
      </b-col>

      <b-col cols="3"
        v-show="['heatmap','linechart','clustermap'].includes(form.type)"
      >
        <div class="section-header">
          <h1 class="heading">Y-axis</h1>

          <div class="actions"></div>
        </div>
        <ExploreAnalysisCreateAxis
          :axis="axis.y"
          :multiple="true"
        ></ExploreAnalysisCreateAxis>
      </b-col>

      <b-col v-show="form.type" cols="3">
        <div
          v-if="
            ['heatmap', 'onedheatmap', 'barchart', 'columnchart','clustermap'].includes(
              form.type
            )
          "
          class="mb-4"
        >
          
          <div class="section-header">
            <h1 class="heading">Clustering</h1>

            <div class="actions"></div>
          </div>
          <b-form-checkbox v-model="form.addClustering" size="sm" switch
            >Add clustering</b-form-checkbox
          >
        </div>

        <div v-if="form.type !== 'linechart'">
           <div class="section-header">
            <h1 class="heading">Subset</h1>

            <div class="actions"></div>
          </div>
          <b-form-checkbox v-model="form.includeSubset" size="sm" switch
            >Include subset</b-form-checkbox
          >
          <ExploreAnalysisCreateAxis
            v-if="form.includeSubset"
            :axis="axis.z"
            :multiple="true"
            class="mt-4"
          ></ExploreAnalysisCreateAxis>
        </div>
      </b-col>
    </b-row>

    <div class="text-right form-footer">
      <b-btn
        variant="primary"
        size="lg"
        :disabled="!allowSubmit"
        @click="submit"
      >
        <i class="fa fa-chart-area"></i> Save
      </b-btn>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import ExploreAnalysisCreateAxis from "../Shared/ExploreAnalysisCreateAxis";
import Slider from "../Shared/Slider";

export default {
  name: "ExploreAnalysisForm",
  components: {
    ExploreAnalysisCreateAxis,
    Slider
  },
  props: {
    allowSubmit: {
      type: Boolean,
      required: true
    },
    axis: {
      type: Object,
      required: true
    },
    form: {
      type: Object,
      required: true
    },
    yearRange: {
      type: Array,
      required: false,
      default: null
    },
    yearLimit: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      years: this.yearLimit,
      types: [
        { value: "scatterplot", text: "Scatterplot" },
        { value: "worldmap", text: "Worldmap" },
        { value: "heatmap", text: "Heatmap" },
        { value: "clustermap", text: "Clustermap" },
        { value: "onedheatmap", text: "1D heatmap" },
        { value: "barchart", text: "Bar chart" },
        { value: "columnchart", text: "Column chart" },
        { value: "linechart", text: "Line chart" }
      ]
    };
  },
  computed: {
    ...mapState({
      metas: state => state.ttc.metas
    }),
    typeOptions() {
      if (this.metas["Location"] || this.metas["Country"] || this.metas["Country Code"]) {
        return this.types;
      }
      return this.types.filter(t => t.value !== "worldmap");
    }
  },
  watch: {
    years(value) {
      if (this.form.startyear !== value[0]) {
        this.form.startyear = value[0];
      }
      if (this.form.recentyear !== value[1]) {
        this.form.recentyear = value[1];
      }
      if (this.form.stopyear !== value[2]) {
        this.form.stopyear = value[2];
      }
    }
  },
  methods: {
    submit() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          this.$toasted.show(this.$validator.errors.items[0].msg, {
            type: "error"
          });
          return;
        }

        this.$emit("callback");
      });
    },
    xLabel(){
      if(this.form.type === 'columnchart' || this.form.type === 'heatmap' || this.form.type === 'linechart' || this.form.type === 'clustermap'){
        return 'X-Axis'
      }
      else if(this.form.type === 'barchart'){
        return 'Y-Axis'
      }
      else{
        return 'Axis'
      }
    }
  }
};
</script>
